import React, { useState, useEffect } from 'react';
import './ConfigModal.css';

export interface ConfigModalData {
  type: 'global' | 'product';
  key: string;
  value: string;
  productId?: string;
  isNew: boolean;
  originalKey?: string;
  originalProductId?: string;
}

interface ConfigModalProps {
  data: ConfigModalData;
  uniqueProductIds: string[];
  onSave: (data: ConfigModalData) => void;
  onClose: () => void;
  isSaving: boolean;
}

const ConfigModal: React.FC<ConfigModalProps> = ({
  data: initialData,
  uniqueProductIds,
  onSave,
  onClose,
  isSaving,
}) => {
  const [formData, setFormData] = useState<ConfigModalData>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  const isBooleanConfig = formData.key.trim().toLowerCase().endsWith('enabled');

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.key.trim()) {
      newErrors.key = 'Key is required';
    }

    if (isBooleanConfig) {
      if (formData.value !== 'true' && formData.value !== 'false') {
        newErrors.value = 'Value is required';
      }
    } else if (!formData.value.trim()) {
      newErrors.value = 'Value is required';
    }

    if (formData.type === 'product' && !formData.productId?.trim()) {
      newErrors.productId = 'Product ID is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content config-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {formData.isNew ? 'Add' : 'Edit'} {formData.type === 'global' ? 'Global' : 'Product'} Configuration
          </h2>
          <button className="close-button" onClick={handleClose} type="button">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p className="modal-description">
              {formData.isNew
                ? `Create a new ${formData.type === 'global' ? 'global' : 'product-specific'} configuration entry.`
                : `Edit the ${formData.type === 'global' ? 'global' : 'product-specific'} configuration entry.`}
            </p>

            {formData.type === 'product' && (
              <div className="form-group">
                <label htmlFor="productId">
                  Product ID <span className="required">*</span>
                </label>
                {formData.isNew ? (
                  <select
                    id="productId"
                    className="form-select"
                    value={formData.productId || ''}
                    onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                    disabled={isSaving}
                  >
                    <option value="">Select Product</option>
                    {uniqueProductIds.map((id) => (
                      <option key={id} value={id}>
                        {id}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    id="productId"
                    className="form-input"
                    value={formData.productId || ''}
                    disabled
                  />
                )}
                {errors.productId && <div className="form-error">{errors.productId}</div>}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="key">
                Key <span className="required">*</span>
              </label>
              <input
                type="text"
                id="key"
                className="form-input"
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                disabled={!formData.isNew || isSaving}
                placeholder="config.key.name"
              />
              {errors.key && <div className="form-error">{errors.key}</div>}
              {formData.isNew && (
                <small className="form-hint">
                  Choose a descriptive key name (e.g., trading.max_order_size)
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="value">
                Value <span className="required">*</span>
              </label>
              {isBooleanConfig ? (
                <select
                  id="value"
                  className="form-select"
                  value={formData.value === 'true' || formData.value === 'false' ? formData.value : ''}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  disabled={isSaving}
                >
                  <option value="">Select…</option>
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              ) : (
                <input
                  type="text"
                  id="value"
                  className="form-input"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  disabled={isSaving}
                  placeholder="Configuration value"
                />
              )}
              {errors.value && <div className="form-error">{errors.value}</div>}
              <small className="form-hint">
                Enter the configuration value
              </small>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="modal-pill-button modal-pill-cancel"
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="modal-pill-button modal-pill-save"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfigModal;
